import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { publicDataFetch } from '../api';

const { FiLoader, FiAlertCircle, FiCheck, FiPlay, FiBookOpen, FiChevronRight, FiAward, FiClock } = FiIcons;

const getEmbedUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})/);
  if (ytMatch && ytMatch[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch && vimeoMatch[1]) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch && driveMatch[1]) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  if (url.includes('app.dadan.io')) return url;
  if (url.includes('embed') || url.endsWith('.mp4')) return url;
  return null;
};

const SharedModule = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moduleData, setModuleData] = useState(null);
  
  const [mode, setMode] = useState('content'); // 'content', 'quiz', 'result'
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);

  useEffect(() => {
    const fetchSharedModule = async () => {
      try {
        const res = await publicDataFetch(`read/module_share_tokens?token=${token}`);
        if (res?.status === 'success' && res.data && res.data.length > 0) {
          const data = res.data[0];
          
          // Check expiration
          const expiresAt = new Date(data.expires_at);
          if (new Date() > expiresAt) {
            setError('This shared link has expired. Links are only valid for 24 hours.');
            setLoading(false);
            return;
          }

          setModuleData(data);
          
          try {
            const parsedQuestions = data.quiz_questions ? JSON.parse(data.quiz_questions) : [];
            setQuestions(parsedQuestions);
          } catch (e) {
            setQuestions([]);
          }
        } else {
          setError('Invalid or missing share token.');
        }
      } catch (err) {
        setError('An error occurred while loading the shared module.');
      } finally {
        setLoading(false);
      }
    };

    fetchSharedModule();
  }, [token]);

  const handleCompleteContent = () => {
    if (questions.length > 0) {
      setMode('quiz');
    } else {
      setScore(100);
      setMode('result');
    }
  };

  const handleQuizSubmit = () => {
    let correct = 0;
    questions.forEach((q, i) => {
      if (answers[i] === q.answer) correct++;
    });
    const pct = (correct / questions.length) * 100;
    setScore(Math.round(pct));
    setMode('result');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center text-slate-500">
        <SafeIcon icon={FiLoader} className="animate-spin text-5xl mb-4 text-blue-600" />
        <p className="font-medium text-lg">Loading module...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 text-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-slate-100">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiAlertCircle} className="text-3xl" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-500 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/login')}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors w-full"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4 md:p-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden border border-slate-200"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-6 py-5 border-b border-slate-200 bg-slate-50/80 gap-4">
          <div>
            <span className="inline-block px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-md uppercase tracking-wider mb-2">
              {moduleData.module_category || 'Training'}
            </span>
            <h2 className="text-xl md:text-2xl font-bold text-slate-800 leading-tight">
              {moduleData.module_title}
            </h2>
          </div>
          {moduleData.module_duration && (
            <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm shrink-0">
              <SafeIcon icon={FiClock} /> {moduleData.module_duration}
            </div>
          )}
        </div>

        <div className="p-6 md:p-8">
          <AnimatePresence mode="wait">
            {mode === 'content' && (
              <motion.div key="content" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-6">
                {(moduleData.module_deliveryFormat === 'Video' || moduleData.module_video_url || moduleData.module_embedVideo) ? (
                  <div className="w-full aspect-video bg-black rounded-xl overflow-hidden shadow-inner">
                    {moduleData.module_embedVideo ? (
                      <div dangerouslySetInnerHTML={{ __html: moduleData.module_embedVideo }} className="w-full h-full" />
                    ) : getEmbedUrl(moduleData.module_video_url) ? (
                      <iframe src={getEmbedUrl(moduleData.module_video_url)} className="w-full h-full border-0" allowFullScreen title="Module Video" />
                    ) : (
                      <div className="flex flex-col items-center justify-center w-full h-full text-slate-400">
                        <SafeIcon icon={FiPlay} className="text-4xl mb-2" />
                        <p className="text-sm">Video preview not available</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-blue-50 text-blue-800 p-6 md:p-8 rounded-xl border border-blue-100 flex items-start gap-4">
                    <SafeIcon icon={FiBookOpen} className="text-4xl text-blue-300 mt-1 shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold mb-2">Reading Material</h3>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{moduleData.module_description}</p>
                    </div>
                  </div>
                )}
                
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-slate-800 mb-2">Module Details</h4>
                  <p className="text-sm text-slate-600 mb-6">{moduleData.module_description}</p>
                  
                  <div className="flex items-center justify-end pt-4 border-t border-slate-200">
                    <button 
                      onClick={handleCompleteContent} 
                      className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors shadow-sm"
                    >
                      {questions.length > 0 ? 'Proceed to Knowledge Check' : 'Finish Module'} <SafeIcon icon={FiChevronRight} />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {mode === 'quiz' && (
              <motion.div key="quiz" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="max-w-2xl mx-auto space-y-6">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-slate-800">Knowledge Check</h3>
                  <p className="text-slate-500 mt-1">Answer the following questions to test your understanding.</p>
                </div>

                {questions.map((q, i) => {
                  let opts = [];
                  try { opts = typeof q.options === 'string' ? JSON.parse(q.options) : q.options; } catch { opts = []; }
                  
                  return (
                    <div key={q.id || i} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                      <p className="font-semibold text-slate-800 mb-4 text-base">
                        <span className="text-blue-600 mr-2">{i + 1}.</span> {q.question}
                      </p>
                      <div className="space-y-3">
                        {opts.filter(o => o?.trim()).map((opt, oIndex) => (
                          <label key={oIndex} className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all ${answers[i] === opt ? 'border-blue-500 bg-blue-50 scale-[1.01]' : 'border-slate-100 hover:border-slate-300 hover:bg-slate-50'}`}>
                            <input 
                              type="radio" 
                              name={`q-${i}`} 
                              checked={answers[i] === opt} 
                              onChange={() => setAnswers(prev => ({ ...prev, [i]: opt }))} 
                              className="accent-blue-600 w-4 h-4" 
                            />
                            <span className="text-sm font-medium text-slate-700">{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div className="flex justify-end pt-6 border-t border-slate-100">
                  <button 
                    onClick={handleQuizSubmit} 
                    disabled={Object.keys(answers).length < questions.length}
                    className={`flex items-center gap-2 px-8 py-3 font-bold rounded-xl transition-all ${Object.keys(answers).length === questions.length ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                  >
                    <SafeIcon icon={FiCheck} /> Submit Answers
                  </button>
                </div>
              </motion.div>
            )}

            {mode === 'result' && (
              <motion.div key="result" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center justify-center py-16 text-center">
                <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className={`w-28 h-28 rounded-full flex items-center justify-center mb-6 shadow-inner ${score >= 80 ? 'bg-emerald-100 text-emerald-500' : score >= 50 ? 'bg-amber-100 text-amber-500' : 'bg-red-100 text-red-500'}`}>
                  <SafeIcon icon={FiAward} className="text-6xl" />
                </motion.div>
                <h3 className="text-3xl font-bold text-slate-800 mb-2">Module Completed!</h3>
                {questions.length > 0 && (
                  <p className="text-slate-500 mb-8 text-lg">You scored <span className="font-bold text-slate-800">{score}%</span> on the knowledge check.</p>
                )}
                <p className="text-sm text-slate-400 max-w-sm mx-auto mb-8">
                  This was a shared preview module. Your progress has not been recorded to an account.
                </p>
                <button onClick={() => window.close()} className="px-8 py-3 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl transition-colors shadow-lg">
                  Close Window
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default SharedModule;